import { Link } from "react-router-dom";
import { ArrowLeft, GithubLogo, DeviceMobile, Terminal, Warning } from "@phosphor-icons/react";

import Header from "@/components/Header";
import HeadlineTicker from "@/components/HeadlineTicker";

export default function AndroidDownloadPage() {
  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900 relative">
      <Header />
      <HeadlineTicker />

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-10 pb-4 relative z-10">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-700 hover:text-zinc-950 mb-6"
        >
          <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
          BACK TO FACT-CHECKER
        </Link>
        <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 mb-3 flex items-center gap-2">
          <DeviceMobile weight="fill" className="w-3.5 h-3.5" />
          TRUESTREAM ANDROID · v1.0
        </div>
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tight text-zinc-950 mb-4">
          Zero taps. The bubble knows.
        </h1>
        <p className="text-zinc-700 text-lg max-w-2xl leading-relaxed">
          Watchdog Mode is an Accessibility Service scoped to only YouTube, TikTok, Instagram, X,
          Facebook, Spotify, YouTube Music, and Netflix. It auto-detects the video you&apos;re
          watching, fact-checks it in the background, and turns the bubble into a live verdict
          signal — green (true), red (false), amber (partial), gray (unverifiable). Verdicts can
          also be read aloud for vision-impaired users.
        </p>
      </section>

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-4 relative z-10">
        <div
          data-testid="build-in-container-notice"
          className="border-2 border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 flex items-start gap-3 mb-8"
        >
          <Warning weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-[13px] leading-relaxed">
            The Android app is currently distributed as a sideload for beta testers. A signed Play
            Store release is on the way — for now, pick the build path below that fits your setup.
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Path 1 */}
        <div className="border-2 border-black bg-white p-5 shadow-[6px_6px_0px_rgba(0,0,0,1)]">
          <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 mb-2">PATH 1 · RECOMMENDED</div>
          <h3 className="font-display text-2xl text-zinc-950 mb-2 flex items-center gap-2">
            <GithubLogo weight="fill" className="w-6 h-6" />
            GitHub Actions
          </h3>
          <p className="text-[13px] text-zinc-700 leading-relaxed mb-4">
            Push the project to GitHub. The included workflow builds a signed debug APK in about
            four minutes. Download from the Actions tab.
          </p>
        </div>

        {/* Path 2 */}
        <div className="border-2 border-black bg-white p-5">
          <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">PATH 2</div>
          <h3 className="font-display text-2xl text-zinc-950 mb-2 flex items-center gap-2">
            <DeviceMobile weight="duotone" className="w-6 h-6" />
            Android Studio
          </h3>
          <p className="text-[13px] text-zinc-700 leading-relaxed mb-4">
            If you have Android Studio installed, this is the smoothest path — about five minutes
            end-to-end.
          </p>
        </div>

        {/* Path 3 */}
        <div className="border-2 border-black bg-white p-5">
          <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">PATH 3</div>
          <h3 className="font-display text-2xl text-zinc-950 mb-2 flex items-center gap-2">
            <Terminal weight="duotone" className="w-6 h-6" />
            Command line
          </h3>
          <p className="text-[13px] text-zinc-700 leading-relaxed mb-4">
            Any Linux/Mac with JDK 17 + Android SDK installed. Run <code className="bg-zinc-100 px-1">./gradlew assembleDebug</code>.
          </p>
        </div>
      </section>

      {/* Walkthrough */}
      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-24 relative z-10">
        <div className="border border-zinc-300 bg-white p-5">
          <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">FIRST INSTALL WALKTHROUGH</div>
          <h4 className="font-display text-lg text-zinc-950 mb-3">Zero-tap sideload checklist</h4>
          <ol className="space-y-2 text-[13px] text-zinc-700 leading-relaxed list-decimal list-inside">
            <li>Settings → Security → <b>Install unknown apps</b> → your file manager → Allow</li>
            <li>Transfer the APK to your phone and tap Install</li>
            <li>Open <b>TrueStream</b> → tap <b>ENABLE OVERLAY</b>. Grant <i>Draw over other apps</i>. Return → tap again to grant <i>Usage access</i>. Return → tap once more to start the bubble.</li>
            <li>Tap the emerald <b>ENABLE WATCHDOG MODE</b> button. Read the honest consent dialog. Tap <b>Continue</b> — Android jumps to Accessibility Settings.</li>
            <li>Find <b>TrueStream Watchdog</b> in the list → toggle ON → confirm the Android system warning.</li>
            <li>(Optional) Turn on <b>Read verdicts aloud</b> in Settings if you want spoken verdicts.</li>
            <li>Open <b>YouTube</b> → play any talking-head video → the bubble color changes automatically: blue while checking, then green / red / amber / gray depending on the verdict.</li>
            <li>Tap the bubble any time to see the compact card with sources.</li>
          </ol>
          <p className="mt-4 text-[12px] text-zinc-500 leading-relaxed">
            <b>Scope reminder:</b> the Accessibility Service cannot read any app that isn&apos;t in
            our published allow-list (YouTube, TikTok, Instagram, X, Facebook, Spotify, YouTube
            Music, Netflix). This is enforced by Android.
          </p>
        </div>
      </section>
    </div>
  );
}
